import { useState } from "react";
import { CalendarIcon } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { formatAgeDisplay, toISODate } from "@/utils/age";

/**
 * DOB Picker — calendar popover tuned for pediatric use.
 *
 * Props:
 *   value       : Date | null — current selected DOB
 *   onChange    : (date: Date) => void
 *   maxAgeYears : number (default 19) — oldest kid we accept
 *   placeholder : string
 *   testId      : data-testid prefix
 *   accent      : hex color for selected-day highlight (defaults to #4ECDC4)
 */
const DOBPicker = ({
  value,
  onChange,
  maxAgeYears = 19,
  placeholder = "Pick date of birth",
  testId = "dob-picker",
  accent = "#4ECDC4",
}) => {
  const [open, setOpen] = useState(false);

  const today = new Date();
  const minDate = new Date(today.getFullYear() - maxAgeYears, today.getMonth(), today.getDate());
  // Default calendar view: 6 years ago (common pediatric visit age)
  const defaultMonth = value || new Date(today.getFullYear() - 6, today.getMonth(), 1);

  const label = value ? formatAgeDisplay(value) : placeholder;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="outline"
          data-testid={`${testId}-trigger`}
          className={cn(
            "w-full justify-start text-left font-normal bg-white/5 border-white/10 text-white hover:bg-white/10 hover:text-white",
            !value && "text-white/40"
          )}
        >
          <CalendarIcon className="mr-2 h-4 w-4" style={{ color: accent }} />
          {value ? (
            <span className="truncate">
              {toISODate(value)} <span className="text-white/40">·</span>{" "}
              <span style={{ color: accent }}>{label}</span>
            </span>
          ) : (
            <span>{placeholder}</span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-auto p-0 bg-[#0f1424] border border-white/10 text-white"
        data-testid={`${testId}-popover`}
      >
        <Calendar
          mode="single"
          selected={value || undefined}
          onSelect={(d) => {
            if (d) {
              onChange(d);
              setOpen(false);
            }
          }}
          defaultMonth={defaultMonth}
          disabled={{ before: minDate, after: today }}
          captionLayout="dropdown-buttons"
          fromYear={today.getFullYear() - maxAgeYears}
          toYear={today.getFullYear()}
          showOutsideDays
          className="text-white"
          classNames={{
            caption: "flex justify-center pt-1 relative items-center text-white",
            caption_label: "hidden",
            caption_dropdowns: "flex gap-2 items-center",
            vhidden: "hidden",
            dropdown: "bg-[#1a1a2e] border border-white/10 text-white rounded-md px-2 py-1 text-xs appearance-none cursor-pointer hover:bg-white/10 focus:outline-none focus:ring-1 focus:ring-[#4ECDC4]",
            dropdown_month: "relative",
            dropdown_year: "relative",
            nav_button: "h-7 w-7 bg-transparent p-0 opacity-60 hover:opacity-100 text-white",
            head_cell: "text-white/40 rounded-md w-8 font-normal text-[0.7rem]",
            day: "h-8 w-8 p-0 font-normal text-white hover:bg-white/10 rounded-md",
            day_selected: "text-[#0a1628] font-semibold hover:text-[#0a1628]",
            day_today: "ring-1 ring-white/30",
            day_outside: "text-white/20",
            day_disabled: "text-white/15 opacity-40",
          }}
          modifiersStyles={{
            selected: { backgroundColor: accent, color: "#0a1628" },
          }}
        />
      </PopoverContent>
    </Popover>
  );
};

export default DOBPicker;
